import {
  FC,
  MutableRefObject,
  ReactElement,
  useEffect,
  useRef,
  useState
} from "react"

import { isClientSide } from "@utils"

export type ObserverProps = {
  children: (ref: MutableRefObject<any>, isObserved: boolean, intersectionRatio: number) => ReactElement<any, any> | null,
  rootMargin?: string,
  numSteps?: number,
  keepConnected?: boolean,
}

const Observer: FC<ObserverProps> = ({
  children,
  numSteps,
  rootMargin,
  keepConnected,
}) => {
  const [isObserved, setIsObserved] = useState<boolean>(false)
  const [intersectionRatio, setIntersectionRatio] = useState<number>(0)
  const ref = useRef()

  useEffect(() => {
    if (!isClientSide() || !ref.current) return

    const onObserved = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        // Only provide the intersectionRatio if a `numStep` (number of steps)
        // have been requested via props
        if (numSteps) setIntersectionRatio(entry.intersectionRatio)

        if (entry.isIntersecting) {
          setIsObserved(true)
          // If we're not tracking intersection ratio and the keepConnect prop is not set or false, go ahead and
          // disconnect the observer once the child has been observed
          if (!numSteps && !keepConnected) observer.disconnect()
        } else { // Reset state if keepConnected prop is present on the component
          if (keepConnected) setIsObserved(false)
        }
      })
    }

    const observer = new IntersectionObserver(onObserved, {
      rootMargin,
      // Fancy math to generate an array of percentages, e.g. if `numStep === 5`
      // then `threshold` will be `[0, 0.2, 0.4, 0.6, 0.8, 1]`
      threshold: numSteps ? Array(numSteps).fill(null).map((_, i) => i * 1 / numSteps) : 0,
    })

    observer.observe(ref.current)

    if (!keepConnected) return () => { observer.disconnect() }
  }, [ref, keepConnected, numSteps, rootMargin])

  return children(ref, isObserved, intersectionRatio)
}

export default Observer
